import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import AppSearchDropDown from "../common/filter/AppSearchDropDown";
import { FilterValue } from "../../../models/Filters/FilterValue";
import FormSmallTextField from "../common/form/FormTextField";
import FormTitle from "../common/form/FormTitle";
import AppButton from "../common/buttons/AppButton";

interface AddCityDialogProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  cityName: string;
  selectedCountry: FilterValue[];
  selectedState: FilterValue[];
  manualStateName: string;
  manualStateCode: number | null;
  popupCountryFilter: FilterValue[];
  popupStateFilter: FilterValue[];
  onCityNameChange: (value: string) => void;
  onCountrySelect: (country: FilterValue[]) => void;
  onStateSelect: (state: FilterValue[]) => void;
  onManualStateNameChange: (value: string) => void;
  onManualStateCodeChange: (value: number | null) => void;
  onCountrySearch: (searchText?: string) => void;
  onStateSearch: (searchText?: string) => void;
  onSave: () => void;
  onReset: () => void;
}

const AddCityDialog: React.FC<AddCityDialogProps> = ({
  open,
  onClose,
  isLoading,
  cityName,
  selectedCountry,
  selectedState,
  manualStateName,
  manualStateCode,
  popupCountryFilter,
  popupStateFilter,
  onCityNameChange,
  onCountrySelect,
  onStateSelect,
  onManualStateNameChange,
  onManualStateCodeChange,
  onCountrySearch,
  onStateSearch,
  onSave,
  onReset,
}) => {
  const handleClose = () => {
    onReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600, pb: 1 }}>
        Add New City
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <FormTitle title="Country" fontSize="12px" />
            <AppSearchDropDown
              isMultiSelect={false}
              required
              title="Select Country"
              value={selectedCountry}
              fontSize="12px"
              list={popupCountryFilter}
              local={false}
              handleSearch={onCountrySearch}
              handleSelect={onCountrySelect}
            />
          </Box>

          <Box>
            <FormTitle title="State" fontSize="12px" />
            <AppSearchDropDown
              isMultiSelect={false}
              required
              title="Select State"
              value={selectedState}
              fontSize="12px"
              list={[{ id: "other", value: "Other" }, ...popupStateFilter]}
              local={false}
              handleSearch={onStateSearch}
              handleSelect={onStateSelect}
              disabled={selectedCountry.length === 0}
            />
            {selectedState.length > 0 && selectedState[0].id === "other" && (
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Box sx={{ width: "70%" }}>
                  <FormTitle title="Enter State Name" fontSize="12px" />
                  <FormSmallTextField
                    required
                    placeholder="Enter state name"
                    fontSize="12px"
                    variant="outlined"
                    label="State Name"
                    value={manualStateName}
                    onChange={(id, value) =>
                      onManualStateNameChange(value || "")
                    }
                  />
                </Box>
                <Box sx={{ width: "30%" }}>
                  <FormTitle title="Enter State Code" fontSize="12px" />
                  <FormSmallTextField
                    required
                    placeholder="Enter state name"
                    fontSize="12px"
                    variant="outlined"
                    label="State Code"
                    value={manualStateCode || ""}
                    onChange={(id, value) =>
                      onManualStateCodeChange(Number(value))
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Box>
            <FormTitle title="City Name" fontSize="12px" />
            <FormSmallTextField
              required
              placeholder="Enter city name"
              fontSize="12px"
              variant="outlined"
              label="City Name"
              value={cityName}
              onChange={(id, value) => onCityNameChange(value || "")}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <AppButton
          label="Cancel"
          variant="grayscale"
          onClick={handleClose}
          customStyles={{ minWidth: 100 }}
        />

        {isLoading ? (
          <AppButton
            label={
              <>
                <CircularProgress size={14} color="inherit" /> Saving...
              </>
            }
            variant="primaryBlock"
            customStyles={{ minWidth: 100 }}
            onClick={() => {}}
          />
        ) : (
          <AppButton
            label="Save City"
            variant="primaryBlock"
            onClick={onSave}
            customStyles={{ minWidth: 100 }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCityDialog;
